import React, { useState, useEffect, FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { publicApi, ApiError } from "../services/api";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./ResetPasswordPage.css";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setTokenValid(false);
      setError("Invalid or missing reset token");
      return;
    }

    const validate = async () => {
      setIsValidating(true);
      setError("");
      try {
        const data = await publicApi.validateResetToken(token);
        setEmail(data.email);
        setTokenValid(true);
      } catch (err) {
        setTokenValid(false);
        setError(err instanceof ApiError ? err.message : "Invalid or expired token");
      } finally {
        setIsValidating(false);
      }
    };

    validate();
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setIsSubmitting(true);
    try {
      await publicApi.resetPassword(token, password);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to reset password");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-background">
          <div className="reset-password-orb reset-password-orb-1"></div>
          <div className="reset-password-orb reset-password-orb-2"></div>
          <div className="reset-password-orb reset-password-orb-3"></div>
        </div>

        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="reset-password-loading">
              <span className="reset-password-spinner"></span>
              <p>Validating reset link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-background">
          <div className="reset-password-orb reset-password-orb-1"></div>
          <div className="reset-password-orb reset-password-orb-2"></div>
          <div className="reset-password-orb reset-password-orb-3"></div>
        </div>

        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="reset-password-header">
              <div className="reset-password-logo">
                <SearchIcon className="reset-password-logo-icon" sx={{ fontSize: 32 }} />
              </div>
              <h1>Invalid Reset Link</h1>
              <p className="reset-password-subtitle">
                This password reset link is invalid or has expired.
              </p>
            </div>

            <div className="reset-password-error">
              <WarningIcon className="reset-password-error-icon" fontSize="small" />
              {error}
            </div>

            <div className="reset-password-footer">
              <p className="reset-password-link">
                <Link to="/forgot-password">Request a new reset link</Link>
              </p>
              <p className="reset-password-link">
                <Link to="/login">Back to Sign In</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-background">
          <div className="reset-password-orb reset-password-orb-1"></div>
          <div className="reset-password-orb reset-password-orb-2"></div>
          <div className="reset-password-orb reset-password-orb-3"></div>
        </div>

        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="reset-password-header">
              <div className="reset-password-logo">
                <SearchIcon className="reset-password-logo-icon" sx={{ fontSize: 32 }} />
              </div>
              <h1>Password reset successful</h1>
              <p className="reset-password-subtitle">
                Your password has been updated. You can now sign in with your new password.
              </p>
            </div>

            <div className="reset-password-success">
              <CheckCircleIcon className="reset-password-success-icon" fontSize="medium" />
              <p>You can now sign in to your account.</p>
            </div>

            <div className="reset-password-footer">
              <p className="reset-password-link">
                <Link to="/login">Sign In</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-background">
        <div className="reset-password-orb reset-password-orb-1"></div>
        <div className="reset-password-orb reset-password-orb-2"></div>
        <div className="reset-password-orb reset-password-orb-3"></div>
      </div>

      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <div className="reset-password-logo">
              <SearchIcon className="reset-password-logo-icon" sx={{ fontSize: 32 }} />
            </div>
            <h1>Reset Password</h1>
            <p className="reset-password-subtitle">
              Enter your new password below.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="reset-password-form">
            {error && (
              <div className="reset-password-error">
                <WarningIcon className="reset-password-error-icon" fontSize="small" />
                {error}
              </div>
            )}

            <div className="reset-password-form-group">
              <label htmlFor="email">Email</label>
              <div className="reset-password-input-wrapper">
                <EmailIcon className="reset-password-input-icon" fontSize="small" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  readOnly
                  disabled
                  className="reset-password-input-readonly"
                />
              </div>
            </div>

            <div className="reset-password-form-group">
              <label htmlFor="password">New Password</label>
              <div className="reset-password-input-wrapper">
                <LockIcon className="reset-password-input-icon" fontSize="small" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="reset-password-form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="reset-password-input-wrapper">
                <LockIcon className="reset-password-input-icon" fontSize="small" />
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <button
              type="submit"
              className="reset-password-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="reset-password-button-spinner"></span>
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>

          <div className="reset-password-footer">
            <p className="reset-password-link">
              <Link to="/login">Back to Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
